import type { Recur, Task } from '../store/types';
import { parseYmd, SEMANA_CURTA } from './date';
import { t } from './i18n';

const diffDias = (a: string, b: string) => Math.round((parseYmd(b).getTime() - parseYmd(a).getTime()) / 864e5);

/** A tarefa acontece nesta data? */
export function occursOn(t: Pick<Task, 'start' | 'end' | 'recur' | 'skip'>, date: string) {
  if (date < t.start) return false;
  if (t.end && date > t.end) return false;
  if (t.skip.includes(date)) return false;
  const r = t.recur, d = parseYmd(date), s = parseYmd(t.start);
  switch (r.type) {
    case 'none': return t.end ? true : date === t.start;      // com fim: tarefa de vários dias
    case 'daily': return true;
    case 'weekly': return (r.days?.length ? r.days : [s.getDay()]).includes(d.getDay());
    case 'monthly': {
      const alvo = r.monthDay ?? s.getDate();
      const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      return d.getDate() === Math.min(alvo, ultimo);
    }
    case 'yearly': return d.getMonth() === s.getMonth() && d.getDate() === s.getDate();
    case 'custom': {
      const n = Math.max(1, r.every ?? 1);
      if (r.unit === 'semana') {
        // a cada N semanas: no mesmo dia da semana do início ou no dia escolhido
        const dia = r.days?.length ? r.days[0] : s.getDay();
        if (d.getDay() !== dia) return false;
        const segIni = new Date(s); segIni.setDate(s.getDate() - s.getDay());
        return Math.floor(Math.round((d.getTime() - segIni.getTime()) / 864e5) / 7) % n === 0;
      }
      if (r.unit === 'mes') {
        // a cada N meses: no mesmo dia do mês do início ou no dia escolhido (no mês curto, o último dia)
        const meses = (d.getFullYear() - s.getFullYear()) * 12 + d.getMonth() - s.getMonth();
        if (meses % n) return false;
        const alvo = r.monthDay ?? s.getDate();
        const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        return d.getDate() === Math.min(alvo, ultimo);
      }
      return diffDias(t.start, date) % n === 0;
    }
  }
}

export const isRepeating = (t: Pick<Task, 'recur' | 'end' | 'start'>) => t.recur.type !== 'none' || (!!t.end && t.end !== t.start);

export function recurLabel(r: Recur, start: string) {
  switch (r.type) {
    case 'none': return t('Não se repete');
    case 'daily': return t('Todo dia');
    case 'weekly': {
      const days = r.days?.length ? r.days : [parseYmd(start).getDay()];
      if (days.length === 7) return t('Semanal · todos os dias');
      if (days.length === 5 && [1, 2, 3, 4, 5].every(x => days.includes(x))) return t('Semanal · dias úteis');
      return t('Semanal · {dias}', { dias: [...days].sort().map(x => t(SEMANA_CURTA[x])).join(', ') });
    }
    case 'monthly': return t('Todo mês · dia {d}', { d: r.monthDay ?? parseYmd(start).getDate() });
    case 'yearly': return t('Todo ano');
    case 'custom': {
      const n = r.every ?? 1;
      if (r.unit === 'semana') return r.days?.length ? t('A cada {n} semana(s) · {dia}', { n, dia: t(SEMANA_CURTA[r.days[0]]) }) : t('A cada {n} semana(s)', { n });
      if (r.unit === 'mes') return r.monthDay ? t('A cada {n} mês(es) · dia {d}', { n, d: r.monthDay }) : t('A cada {n} mês(es)', { n });
      return t('A cada {n} dia(s)', { n });
    }
  }
}
